// yjsProvider.js
// Yjs WebSocket provider for collaboration

import {WebsocketProvider} from "y-websocket";
import {toAwarenessUser} from "../helpers/utils";
import {getAccessToken} from '../../../auth/jwtAuth';
import {getOrCreateSession} from "../../../session/awpSessionContext";
import {installCollaborationConnectionGuard} from "./collaborationConnectionGuard";

// Convert http(s) urls (or bare hosts) to ws(s) urls, defaulting to wss on https pages.
function normalizeWebsocketUrl(inputUrl) {
    const url = (inputUrl || '').trim();

    // If caller already provided ws/wss, keep it.
    if (/^wss?:\/\//i.test(url)) return url;

    // If caller provided http/https, convert to ws/wss.
    if (/^https?:\/\//i.test(url)) {
        return url.replace(/^http:/i, 'ws:').replace(/^https:/i, 'wss:');
    }

    // If it's a bare host/path, prefix based on current page scheme.
    const pageIsHttps = typeof window !== 'undefined' && window.location && window.location.protocol === 'https:';
    const protocol = pageIsHttps ? 'wss://' : 'ws://';
    return protocol + url.replace(/^\/+/, '');
}

/**
 * Create a Yjs WebSocket provider for real-time collaboration
 * @param {Object} options - Configuration options
 * @param {string} options.documentId - Document identifier
 * @param {Object} options.ydoc - Yjs document
 * @param {Object} options.user - User object
 * @param {string} options.websocketUrl - WebSocket server URL (optional, defaults to localhost:8080)
 * @returns {WebsocketProvider} Yjs WebSocket provider
 */
export function createYjsProvider(options) {
    const {
        documentId,
        ydoc,
        user,
        websocketUrl = process.env.REACT_APP_WEBSOCKET_URL || 'wss://awp.minsky.ai/ws/collab',
    } = options;

    const normalizedWebsocketUrl = normalizeWebsocketUrl(websocketUrl);

    // Pass JWT token as a query param via y-websocket's `params` option.
    // This ensures y-websocket appends /{room} to the base URL first, then
    // adds ?token=... — preventing the token from corrupting the path and
    // causing the server to receive an empty room (docName = 'default').
    const token = getAccessToken ? getAccessToken() : null;
    const params = token ? {token} : {};

    console.log('[y-websocket] Creating provider with URL:', normalizedWebsocketUrl);
    console.log('[y-websocket] Document ID:', documentId);

    const provider = new WebsocketProvider(
        normalizedWebsocketUrl,
        documentId,
        ydoc,
        {connect: true, params}
    );

    installCollaborationConnectionGuard(provider);

    // Add error handler to catch protocol errors
    if (provider.ws) {
        provider.ws.addEventListener('error', (error) => {
            console.error('[y-websocket] WebSocket error:', error);
        });
        provider.ws.addEventListener('open', () => {
            console.log('[y-websocket] WebSocket OPENED. URL:', provider.ws.url);
        });
    }

    // Listen for WebSocket connection to add error handling
    provider.on('status', ({status}) => {
        console.log('[y-websocket] Status changed to:', status);
        if (status === 'connected' && provider.ws) {
            console.log('[y-websocket] Connected to:', provider.ws.url);
            // Wrap the onmessage handler to catch protocol errors
            const originalOnMessage = provider.ws.onmessage;
            if (originalOnMessage && !provider.ws._errorHandlerAdded) {
                provider.ws._errorHandlerAdded = true;
                provider.ws.onmessage = function (event) {
                    try {
                        originalOnMessage.call(this, event);
                    } catch (error) {
                        if (error.message && error.message.includes('Unknown message type')) {
                            console.error('[y-websocket] ============================================');
                            console.error('[y-websocket] Protocol error - server sent unknown message type');
                            console.error('[y-websocket] WebSocket URL:', provider.url);
                            console.error('[y-websocket] WebSocket readyState:', this.readyState);
                            console.error('[y-websocket] Message data type:', typeof event.data);
                            console.error('[y-websocket] Message data:', event.data);
                            if (event.data instanceof ArrayBuffer) {
                                const view = new Uint8Array(event.data);
                                const bytes = Array.from(view.slice(0, 20));
                                console.error('[y-websocket] First bytes:', bytes);

                                // Decode the message to help debug
                                if (bytes.length > 0) {
                                    const messageType = bytes[0];
                                    console.error('[y-websocket] Message type byte:', messageType);

                                    if (messageType === 0) {
                                        console.error('[y-websocket] This is a SYNC message (type 0)');
                                        if (bytes.length > 1) {
                                            console.error('[y-websocket] Sync step byte:', bytes[1]);
                                            console.error('[y-websocket] Expected sync step: 0, 1, or 2');
                                            console.error('[y-websocket] Got:', bytes[1]);
                                            if (bytes[1] > 2) {
                                                console.error('[y-websocket] ❌ INVALID SYNC STEP! Server is encoding messages incorrectly.');
                                            }
                                        }
                                    } else if (messageType === 1) {
                                        console.error('[y-websocket] This is an AWARENESS message (type 1)');
                                    } else if (messageType === 2) {
                                        console.error('[y-websocket] This is an AUTH message (type 2)');
                                    } else if (messageType === 3) {
                                        console.error('[y-websocket] This is a QUERY AWARENESS message (type 3)');
                                    } else {
                                        console.error('[y-websocket] ❌ UNKNOWN MESSAGE TYPE:', messageType);
                                        console.error('[y-websocket] Valid types are: 0 (sync), 1 (awareness), 2 (auth), 3 (query awareness)');
                                    }
                                }
                            }
                            console.error('[y-websocket] ============================================');
                            console.error('[y-websocket] Your server is NOT implementing the y-websocket protocol correctly.');
                            console.error('[y-websocket] See SERVER_IS_WRONG.md for detailed analysis and fix.');
                            console.error('[y-websocket] Raw error:', error);
                        } else {
                            // Re-throw other errors
                            throw error;
                        }
                    }
                };
            }
        }
    });

    const applyAwarenessUser = (sessionUser) => {
        if (!sessionUser?.uid) {
            return;
        }
        const {sessionId, sessionStart} = getOrCreateSession(sessionUser.uid);
        provider.awareness.setLocalStateField(
            "user",
            toAwarenessUser(sessionUser, {
                clientSessionId: sessionId,
                clientSessionStart: sessionStart,
            })
        );
    };

    applyAwarenessUser(user);

    return provider;
}
